import {emailValidator, setBoolInput, setSelectValue} from 'js/reusable/formHelper';
import {__, options} from 'js/tool/page/current';
import {getAccountTypeOptions} from 'js/admin/helpers/overlay/overlay';

export function getStepTexts(step, isNotRep) {
	switch (step) {
		case 0:
			return OFFICE_STEP_TEXTS;
		case 1:
			return COMPANY_STEP_TEXTS;
		case 2:
			return getBankAccountStepTexts();
		case 3:
			return LOCAL_FIELDS_STEP_TEXTS;
		case 4:
			if (isNotRep) {
				return getNotRepStepTexts();
			}

			return getRepStepTexts();
		case 5:
			return AGREEMENT_STEP_TEXTS;
		case 6:
			if (isNotRep) {
				return FINISH_TEXTS_NO_REP;
			}

			return FINISH_TEXTS;
		default:
			throw new Error('Step ' + step + ' is invalid');
	}
}

const OFFICE_STEP_TEXTS = {
	back:  null,
	next:  'fb:company info',
	title: 'fb:business info',
	icon:  'fas fa-chevron-right',
};

const COMPANY_STEP_TEXTS = {
	back:  'fb:business info',
	next:  'fb:tax and bank info',
	title: 'fb:company',
	icon:  'fas fa-chevron-right',
};

function getBankAccountStepTexts() {
	return {
		back:  'fb:company info',
		title: 'fb:tax and bank info',
		next:  localFieldsStepVisible() ? 'fb:local fields' : 'fb:legal representative',
		icon:  'fas fa-chevron-right',
	};
}

const LOCAL_FIELDS_STEP_TEXTS = {
	back:  'fb:tax and bank info',
	title: 'fb:local fields',
	next:  'fb:legal representative',
	icon:  'fas fa-chevron-right',
};

function getRepStepTexts() {
	return {
		back:  localFieldsStepVisible() ? 'fb:local fields' : 'fb:tax and bank info',
		next:  'fb:agreement',
		title: 'fb:legal representative',
		icon:  'fas fa-chevron-right',
	};
}

function getNotRepStepTexts() {
	return {
		back:  localFieldsStepVisible() ? 'fb:local fields' : 'fb:tax and bank info',
		next:  'fb:submit',
		title: 'fb:legal representative',
		icon:  'fas fa-check',
	};
}

const AGREEMENT_STEP_TEXTS = {
	back:  'fb:legal representative',
	next:  'fb:accept',
	title: 'fb:agreement',
	icon:  'fas fa-check',
};

const FINISH_TEXTS = {
	back:  null,
	next:  'fb:exit',
	title: 'fb:reg done',
	body:  'fb:reg done body',
	icon:  'fas fa-times',
};

const FINISH_TEXTS_NO_REP = {
	back:  null,
	next:  'fb:exit',
	title: 'fb:reg done',
	body:  'fb:reg done body no rep',
	icon:  'fas fa-times',
};

export function getEContractVariants(country) {
	if (country.value === 'si') {
		return ['EN_SI', 'SI'];
	}

	return ['EN', 'KH'];
}

export function getOfficeData(accountData) {
	const offices = accountData.clientBMs;

	if (!accountData) {
		return;
	}

	const updatedObj = {
		externalAdvertiserData: {required:    false, valueSetter: setSelectValue,
			value:       {
				value: accountData.externalAdvertiserData?.externalAdvertiserId || '',
				label: accountData.externalAdvertiserData?.label || '',
			}},
		clientBMs: {value: offices},
	};

	return updatedObj;
}

export function getCompanyData(accountData) {
	const accDataObject = accountData;

	accountData = Object.keys(accDataObject).reduce((accu, key) => {
		if (key.startsWith('customer')) {
			accu[
				key.replace('customer', '')[0].toLowerCase() +
				key.replace('customer', '').substring(1)
			] = accDataObject[key];
		}

		return accu;
	}, {});

	return {
		name:        {required: false, value: accountData.name || ''},
		surname:     {required: false, value: accountData.surname || ''},
		email:       {required: false, value: accountData.email || '', validator: emailValidator},
		phone:       {required: false, value: accountData.phone || ''},
		emsoNumber:  {required: false, value: accountData.regNumber || ''},
		companyName: {value: accountData.companyName || ''},
		address:     {required: false, value: accountData.address || ''},
		zip:         {required: false, value: accountData.zip || ''},
		city:        {required: false, value: accountData.city || ''},
		accountType: {required: false, valueSetter: setSelectValue, value: getSelectedAccountType(accountData)},
		country:     {
			valueSetter: setSelectValue, value:       {
				value: accountData.countryId ? accountData.countryId : options.country.value,
				label: accountData.countryName ?
					accountData.countryName : options.country.label,
			},
		},
	};
}

export function getLocalFieldsData(accountData) {
	return {
		localName:              {value: accountData.customerLocalName || '', required: false},
		localSurname:           {value: accountData.customerLocalSurname || '', required: false},
		localAddress:           {value: accountData.customerLocalAddress || '', required: false},
		localZip:               {value: accountData.customerLocalZip || '', required: false},
		localCity:              {value: accountData.customerLocalCity || '', required: false},
		localCountry:           {value: accountData.customerLocalCountry || '', required: false},
		localCompanyName:       {value: accountData.customerLocalCompanyName || '', required: false},
		localTaxNumber:         {value: accountData.customerLocalVatNumber || '', required: false},
		localEmsoNumber:        {value: accountData.customerLocalRegNumber || '', required: false},
		localBankName:          {value: accountData.bankLocalName || '', required: false},
		localBankAccountNumber: {value: accountData.bankLocalAccountNumber || '', required: false},
	};
}

export function getLocalFieldVariables(values, customerId) {
	return {
		variables: {
			taUserAccountId:        customerId,
			localName:              values.localName,
			localSurname:           values.localSurname,
			localAddress:           values.localAddress,
			localZip:               values.localZip,
			localCity:              values.localCity,
			localCountry:           values.localCountry,
			localCompanyName:       values.localCompanyName,
			localTaxNumber:         values.localTaxNumber,
			localEmsoNumber:        values.localEmsoNumber,
			localBankName:          values.localBankName,
			localBankAccountNumber: values.localBankAccountNumber,
		},
	};
}

export function getCompanyUpdateVariables(values, customerId) {
	return {
		variables: {
			taUserAccountId: customerId,
			name:            values.name,
			surname:         values.surname,
			email:           values.email,
			emsoNumber:      values.emsoNumber,
			companyName:     values.companyName,
			countryId:       values.country.value,
			phone:           values.phone,
			address:         values.address,
			zip:             values.zip,
			city:            values.city,
			taAccountType:   values.accountType.value,
		},
	};
}

export function getOfficeVariables(values, customerId) {
	const valueObject = values.externalAdvertiserData;

	return {
		variables: {
			taUserAccountId:      customerId,
			externalAdvertiserId: valueObject.value,
		},
	};
}

export function getBankAccountVariables(values, customerId) {
	return {
		variables: {
			taUserAccountId:   customerId,
			taxNumber:         values.taxNumber,
			bankName:          values.bankName,
			bankAccountNumber: values.bankAccountNumber,
			isVatCommited:     Number(values.isVatCommited) === 1,
		},
	};
}

function getSelectedAccountType(accountData) {
	if (accountData.accountType === 'INDIVIDUAL') {
		return {value: 'INDIVIDUAL', label: __('fb:individual acc type')};
	}

	return getAccountTypeOptions()[0];
}

export function getBankAccountData(data) {
	return {
		bankName:          {required: false, value: data.bankName ? data.bankName : ''},
		taxNumber:         {required: true, value: data.customerVatNumber ? data.customerVatNumber : ''},
		bankAccountNumber: {required: false, value: data.bankAccountNumber ? data.bankAccountNumber : ''},
		isVatCommited:     {value: true, valueSetter: setBoolInput, required: false},
	};
}

export function isNextButtonEnabled(validationErrorsMap, step) {
	const stepToName = {
		0: 'office',
		1: 'company',
		2: 'bankAccount',
		3: 'localFields',
		4: 'representative',
		5: 'agreement',
		6: 'finish',
	};

	return !validationErrorsMap[stepToName[step.toString()]] ||
		Object.keys(validationErrorsMap[stepToName[step.toString()]]).length === 0;
}

export function getLegalRepresentativeOptions() {
	return [
		{value: 'YES', label: __('fb:legal representative yes')},
		{value: 'NO', label: __('fb:legal representative no')},
	];
}

export function getRepValue(values, companyValues, name) {
	return values[name]?.length > 0 && values.representative.value === 'NO' ?
		values[name] :
		companyValues[name]
	;
}

export function generateContractHTML(html, valuesMap) {
	Object.keys(valuesMap).forEach((key) => {
		html = html.replace(new RegExp(key, 'g'), valuesMap[key]);
	});

	return html;
}

// eslint-disable-next-line max-len
export const emptyImg = 'data:image/png;base64,iVBORw0KGgoAAAANSUhEUgAAAmQAAABkCAQAAACCh+QeAAABD0lEQVR42u3UMQ0AAAzDsJU/6d2FUMmGkCM5gHGRADAyACMDMDLAyACMDMDIAIwMMDIAIwMwMgAjA4wMwMgAjAzAyAAjAzAyACMDMDLAyACMDMDIAIwMMDIAIwMwMgAjA4wMwMgAjAzAyAAjAzAyACMDMDLAyACMDMDIAIwMMDIAIwMwMgAjA4wMwMgAjAzAyAAjAzAyACMDMDLAyACMDMDIAIwMMDIAIwMwMgAjA4wMwMgAjAzAyAAjAzAyACMDMDLAyACMDMDIAIwMMDIAIwMwMgAjA4wMwMgAjAzAyAAjAzAyACMDMDLAyACMDMDIAIwMMDIAIwMwMgAjA4wMwMgAjAzAyAAjAzAyACMDaA9JLgBlsmh7oQAAAABJRU5ErkJggg==';

export function hasProperty(values, name) {
	return values.hasOwnProperty(name) && (values[name] || values[name] !== 0);
}

export function getInitialStep(accountData) {
	if (!hasProperty(accountData, 'businessId')) {
		return 0;
	}

	if (
		!hasProperty(accountData, 'customerName') ||
		!hasProperty(accountData, 'customerSurname') ||
		!hasProperty(accountData, 'customerEmail') ||
		!hasProperty(accountData, 'customerPhone') ||
		!hasProperty(accountData, 'customerRegNumber') ||
		!hasProperty(accountData, 'customerCompanyName') ||
		!hasProperty(accountData, 'customerAddress') ||
		!hasProperty(accountData, 'customerZip') ||
		!hasProperty(accountData, 'customerCity') ||
		!hasProperty(accountData, 'bankType') ||
		!hasProperty(accountData, 'customerCountryId')
	) {
		return 1;
	}

	if (
		!hasProperty(accountData, 'bankName') ||
		!hasProperty(accountData, 'customerVatNumber') ||
		!hasProperty(accountData, 'bankAccountNumber')
	) {
		return 2;
	}

	return 4;
}

export function localFieldsStepVisible() {
	return options.network === 'pn_fa';
}

export function transformValidationErrors(validationErrors) {
	return Object.keys(validationErrors).reduce((acc, key, index) => {
		acc += __(validationErrors[key]) + (index !== Object.keys(validationErrors).length - 1 ? '\n' : '');

		return acc;
	}, '');
}
